import { ButtonProps } from '../types';
import styles from './StanButton.module.css';
import { createIconElement, stanAnalytics } from '@/lib';
import { cn } from '@/lib/utils/cn';
import { useUser } from '@/queries/useUser';
import { Ref } from 'react';
import { StanLoader } from '../StanLoader/StanLoader';

type StanButtonProps = ButtonProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps> & {
        ref?: Ref<HTMLButtonElement>;
    };

export const StanButton = ({
    name,
    children,
    type = 'button',
    color = 'primary',
    variant,
    size = 'md',
    loading = false,
    disabled = false,
    onClick,
    className = '',
    icon,
    ref,
    ...props
}: StanButtonProps) => {
    const { user } = useUser();
    const getColorClass = () => {
        return styles[color];
    };

    const getVariantClass = () => {
        return variant ? styles[variant] || '' : '';
    };

    const getSizeClass = () => {
        return styles[size];
    };

    const getStateClasses = () => {
        const classes = [];
        if (disabled) classes.push(styles.disabled);
        if (loading) classes.push(styles.loading);
        return classes.join(' ');
    };

    const getIconSizeClass = () => {
        const sizeMap = {
            xs: styles.iconXs,
            sm: styles.iconSm,
            md: styles.iconMd,
            lg: styles.iconLg,
            xl: styles.iconXl,
        };
        return sizeMap[size];
    };

    const variableClasses =
        `${getColorClass()} ${getVariantClass()} ${getSizeClass()} ${getStateClasses()} ${className}`.trim();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        stanAnalytics('admin-button-click', {
            meta: {
                user_id: user?.user_id,
                username: user?.username,
            },
            props: {
                button_name: name,
            },
        });
        onClick?.(e);
    };

    const hasContent = !!children;

    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleClick(e)}
            className={cn(styles['stan-button'], variableClasses, !hasContent && styles['no-content'])}
            {...props}
        >
            {loading ? (
                <StanLoader dotSize={8} color='currentColor' />
            ) : (
                <>
                    {icon &&
                        createIconElement(icon, {
                            className: cn(styles.icon, getIconSizeClass(), hasContent ? 'mr-0.5' : ''),
                        })}
                    {children}
                </>
            )}
        </button>
    );
};

StanButton.displayName = 'StanButton';
